import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Package, Users, CheckCircle, XCircle, ArrowLeft, Eye } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const Admin = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);

  useEffect(() => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    if (!currentUser || currentUser.role !== 'admin') {
      navigate('/');
      return;
    }
    setUser(currentUser);

    loadData();
  }, [navigate]);

  const loadData = () => {
    const allOrders = JSON.parse(localStorage.getItem('orders') || '[]');
    setOrders(allOrders);

    const allUsers = JSON.parse(localStorage.getItem('mockUsers') || '[]');
    const customerUsers = allUsers.filter(u => u.role === 'customer');
    setCustomers(customerUsers);
  };

  const handleApprove = (orderId) => {
    const allOrders = JSON.parse(localStorage.getItem('orders') || '[]');
    const updatedOrders = allOrders.map(order => 
      order.id === orderId ? { ...order, status: 'approved' } : order
    );
    localStorage.setItem('orders', JSON.stringify(updatedOrders));
    setOrders(updatedOrders);
    toast({
      title: 'Onaylandı!',
      description: 'Sipariş başarıyla onaylandı.',
    });
  };

  const handleReject = (orderId) => {
    const allOrders = JSON.parse(localStorage.getItem('orders') || '[]');
    const updatedOrders = allOrders.map(order => 
      order.id === orderId ? { ...order, status: 'rejected' } : order
    );
    localStorage.setItem('orders', JSON.stringify(updatedOrders));
    setOrders(updatedOrders);
    toast({
      title: 'Reddedildi!',
      description: 'Sipariş reddedildi.',
      variant: 'destructive',
    });
  };

  const statusText = {
    pending: 'Onay Bekliyor',
    approved: 'Onaylandı',
    rejected: 'Reddedildi'
  };

  const statusColor = {
    pending: 'bg-yellow-500/10 text-yellow-500 border-yellow-500/30',
    approved: 'bg-green-500/10 text-green-500 border-green-500/30',
    rejected: 'bg-red-500/10 text-red-500 border-red-500/30'
  };

  if (!user) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black py-12 px-4">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <Link to="/" className="inline-flex items-center text-green-400 hover:text-green-300 mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Ana Sayfaya Dön
            </Link>
            <h1 className="text-4xl font-bold text-green-400">Yönetici Paneli</h1>
            <p className="text-gray-400 mt-2">Hoş geldiniz, {user.name}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Toplam Sipariş</p>
                <p className="text-3xl font-bold text-white mt-2">{orders.length}</p>
              </div>
              <div className="bg-green-500/10 p-3 rounded-xl">
                <Package className="w-8 h-8 text-green-400" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-yellow-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Bekleyen Siparişler</p>
                <p className="text-3xl font-bold text-white mt-2">
                  {orders.filter(o => o.status === 'pending').length}
                </p>
              </div>
              <div className="bg-yellow-500/10 p-3 rounded-xl">
                <Package className="w-8 h-8 text-yellow-400" />
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-gray-900 to-black border border-blue-500/30 rounded-2xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-sm">Toplam Müşteri</p>
                <p className="text-3xl font-bold text-white mt-2">{customers.length}</p>
              </div>
              <div className="bg-blue-500/10 p-3 rounded-xl">
                <Users className="w-8 h-8 text-blue-400" />
              </div>
            </div>
          </div>
        </div>

        {/* Orders Management */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-2xl p-6 mb-8">
          <h2 className="text-2xl font-bold text-white mb-6">Sipariş Yönetimi</h2>

          {orders.length === 0 ? (
            <div className="text-center py-12">
              <Package className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Henüz sipariş bulunmamaktadır.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {orders.map((order) => {
                // Dosya kontrolü için değişken
                const proof = order.proofImage || order.paymentProof || order.file;
                
                return (
                  <div
                    key={order.id}
                    className="bg-gray-800/50 rounded-xl p-6 border border-green-500/20"
                  >
                    <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-bold text-white">{order.item.name}</h3>
                          <span className={`inline-flex items-center px-3 py-1 rounded-lg border text-sm ${statusColor[order.status]}`}>
                            {statusText[order.status]}
                          </span>
                        </div>
                        <div className="grid md:grid-cols-2 gap-2 text-sm">
                          <p className="text-gray-400">
                            Müşteri: <span className="text-white">{order.userName}</span>
                          </p>
                          <p className="text-gray-400">
                            Email: <span className="text-white">{order.userEmail}</span>
                          </p>
                          <p className="text-gray-400">
                            Tarih: <span className="text-white">{new Date(order.date).toLocaleDateString('tr-TR')}</span>
                          </p>
                          <p className="text-gray-400">
                            Tutar: <span className="text-green-400 font-semibold">{order.item.price} TL</span>
                          </p>
                          
                          {/* ÖDEME KANITI EKLEMESİ */}
                          {proof && (
                            <div className="col-span-2 mt-3 pt-3 border-t border-white/5 flex items-center gap-4">
                               <p className="text-gray-400">Ödeme Kanıtı:</p>
                               <Button 
                                 variant="outline" 
                                 size="sm" 
                                 className="border-green-500/50 text-green-400 hover:bg-green-500/10"
                                 onClick={() => window.open(proof, '_blank')}
                               >
                                 <Eye className="w-4 h-4 mr-2" />
                                 Dosyayı Gör
                               </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      {order.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApprove(order.id)}
                            className="bg-green-500 hover:bg-green-600 text-white"
                          >
                            <CheckCircle className="w-4 h-4 mr-2" />
                            Onayla
                          </Button>
                          <Button
                            onClick={() => handleReject(order.id)}
                            variant="destructive"
                            className="bg-red-500 hover:bg-red-600"
                          >
                            <XCircle className="w-4 h-4 mr-2" />
                            Reddet
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Customers List */}
        <div className="bg-gradient-to-br from-gray-900 to-black border border-green-500/30 rounded-2xl p-6">
          <h2 className="text-2xl font-bold text-white mb-6">Müşteriler</h2>

          {customers.length === 0 ? (
            <div className="text-center py-12">
              <Users className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <p className="text-gray-400">Henüz müşteri bulunmamaktadır.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {customers.map((customer) => (
                <div
                  key={customer.id}
                  className="bg-gray-800/50 rounded-xl p-4 border border-green-500/20"
                >
                  <h3 className="text-white font-semibold mb-1">{customer.name}</h3>
                  <p className="text-gray-400 text-sm">{customer.email}</p>
                  <p className="text-green-400 text-xs mt-2">
                    Sipariş Sayısı: {orders.filter(o => o.userId === customer.id).length}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;